<script setup lang="ts">
import { inject, ref, watch } from 'vue'

import debounce from 'lodash.debounce'
import { useI18n } from 'vue-i18n'

import BrandImage from '@/components/BrandImage.vue'
import IconBack from '@/components/IconBack.vue'
import IconClear from '@/components/IconClear.vue'
import IconSearch from '@/components/IconSearch.vue'

import fetchGeoLocation from '@/lib/fetchGeoLocation'
import useCenter from '@/lib/useMapCenter'
import type { Coordinates } from '@/types'

const { locale } = useI18n()

const query = ref('')
const showPredictions = ref<boolean>(false)
const predictions = ref<
  {
    location: {
      lat: number
      lng: number
    }
    description: string
  }[]
>([])

const props = defineProps<{
  mapRef: google.maps.Map | undefined
  selectedStore: boolean
  center: Coordinates
  defaultZoom: number
}>()

const setShowPredictions = (bool: boolean) => {
  showPredictions.value = bool
}

const clearQuery = (): void => {
  query.value = ''
  emit('calcDistance', props.center)
}

const { updateCenter } = useCenter()

const emit = defineEmits<{
  (e: 'calcDistance', pos: Coordinates): void
  (e: 'saveCenter', pos: Coordinates): void
  (e: 'removeStore'): void
}>()

const brand = inject('BRAND')

const searchMarker: google.maps.Marker[] = []

const setMarker = (location: { lat: number; lng: number }) => {
  props.mapRef?.panTo(location)
  props.mapRef?.setZoom(20)

  updateCenter(location)

  searchMarker.forEach((marker) => {
    marker.setMap(null)
  })

  searchMarker.push(
    new google.maps.Marker({
      map: props.mapRef,
      position: location,
    }),
  )

  emit('calcDistance', location)
}

const selectFirstPrediction = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    setMarker(predictions.value[0].location)
    setShowPredictions(false)
  }
}

const AutocompleteService = new google.maps.places.AutocompleteService()

watch(
  query,
  debounce(async () => {
    if (query.value === '') return

    const placePredictions = await AutocompleteService.getPlacePredictions({
      input: query.value,
      language: locale.value,
    }).then((response) => response.predictions)

    const geoLocationEnrichedPredictions = []

    for (const prediction of placePredictions) {
      const geoLocation = await fetchGeoLocation(prediction.description)

      geoLocationEnrichedPredictions.push({
        location: geoLocation,
        description: prediction.description,
      })
    }
    setShowPredictions(true)
    predictions.value = geoLocationEnrichedPredictions
  }, 1500),
)
</script>
<template>
  <div
    class="text-gray-medium sticky top-0 z-20 w-full bg-white px-3 pt-3 transition duration-200 focus-within:text-black"
  >
    <IconSearch
      v-if="!selectedStore"
      class="absolute left-6 top-[19px] h-8 w-5 stroke-2 xl:w-6"
      :class="{
        'text-gray-medium': brand === 'satch',
        'text-gray-500': brand !== 'satch',
      }"
    />
    <IconBack
      v-if="selectedStore"
      class="absolute left-6 top-[19px] h-8 w-5 cursor-pointer stroke-2"
      :class="{
        'text-gray-medium': brand === 'satch',
        'text-gray-500': brand !== 'satch',
      }"
      @click="() => emit('removeStore')"
    />
    <IconClear
      v-if="query.length > 0"
      class="absolute right-6 top-[19px] h-8 w-6 cursor-pointer stroke-2"
      @click="clearQuery"
    />
    <input
      id="search-input"
      v-model="query"
      class="w-full appearance-none rounded-tl-xl rounded-tr-xl border py-2.5 pl-10 pr-12 outline-hidden hover:bg-gray"
      type="text"
      autocomplete="false"
      :placeholder="$t('inputSearch')"
      :class="{
        'text-gray-medium border-gray-medium': brand === 'satch',
        'border-gray-500 text-gray-500': brand !== 'satch',
      }"
      @keydown="selectFirstPrediction"
      @focus="() => setShowPredictions(true)"
    />
    <div
      v-if="predictions.length >= 1 && query !== '' && showPredictions"
      id="results"
      class="space-y-2 border-2 border-primary-light shadow-lg"
    >
      <li
        v-for="(prediction, index) in predictions"
        :key="index"
        class="flex cursor-pointer list-none items-center space-x-2 border-b-2 border-gray p-0.5 px-1 last:border-b-0"
        @click="
          () => {
            setMarker(prediction.location)
            setShowPredictions(false)
          }
        "
      >
        <BrandImage
          src="icons/Location.svg"
          alt="location"
          width="16"
        />
        <span class="font-sans text-base md:text-sm">
          {{ prediction.description }}
        </span>
      </li>
    </div>
  </div>
</template>
